import { useEffect, useSyncExternalStore } from 'react';

/**
 * G-TP-06 / GAP-OP-04 — the unsaved-input signal the guidance engine obeys.
 *
 * A page that can hold unsaved input calls `useGuidanceDirty(isDirty)`. While
 * any registration is dirty, the engine never navigates away and offers only a
 * guide on the current page (CONTRACT §2 "Dirty forms"). It is a signal for
 * guidance only: it blocks no navigation the user makes and saves nothing.
 *
 * A tiny external store: one entry per dirty hook instance, so two forms on a
 * page, or the same form mounted twice, never clear each other.
 */
const dirty = new Map<symbol, string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Registers this component's unsaved input while `isDirty` is true, and
 * unregisters when it turns false or the component unmounts.
 *
 * `key` names the form for diagnostics only (e.g. `cart`); it is never stored.
 * The cart also counts as dirty while a field has focus or a blur-save is
 * pending, because a blur there writes.
 */
export function useGuidanceDirty(isDirty: boolean, key = 'form'): void {
  useEffect(() => {
    if (!isDirty) return;

    const token = Symbol(key);
    dirty.set(token, key);
    emit();

    return () => {
      dirty.delete(token);
      emit();
    };
  }, [isDirty, key]);
}

/** Whether any mounted page currently holds unsaved input. */
export function isAnyGuidanceDirty(): boolean {
  return dirty.size > 0;
}

/** Calls `listener` whenever the dirty state may have changed; returns the unsubscribe. */
export function subscribeGuidanceDirty(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/** The same answer as `isAnyGuidanceDirty`, kept current for rendering. */
export function useAnyGuidanceDirty(): boolean {
  return useSyncExternalStore(subscribeGuidanceDirty, isAnyGuidanceDirty, isAnyGuidanceDirty);
}
